/**
 * Market Utility Functions
 * Helper functions for market operations and lookups
 */

import {
  MARKETS,
  getMarketById,
  getMarketByTokenIndex,
  getMarketByMarketIndex,
  getMarketsByCategory,
  getMarketsByLeverage,
  getMarketsWithMarketIndexAndLeverage,
  getCoinIdFromTokenIndex,
  getCoinId,
  formatMarketIndex,
  formatTokenIndex,
  getMarketName,
  isCityMarket,
  getCryptoMarkets,
  getTradableMarkets,
  getStockMarkets,
  getCommodityMarkets,
  isStockMarket,
  isCommodityMarket,
  hasScheduledHours,
  MARKET_CATEGORIES,
} from '../config/markets';

// Re-export all functions from markets config for convenience
export {
  MARKETS,
  getMarketById,
  getMarketByTokenIndex,
  getMarketByMarketIndex,
  getMarketsByCategory,
  getMarketsByLeverage,
  getMarketsWithMarketIndexAndLeverage,
  getCoinIdFromTokenIndex,
  getCoinId,
  formatMarketIndex,
  formatTokenIndex,
  getMarketName,
  isCityMarket,
  getCryptoMarkets,
  getTradableMarkets,
  getStockMarkets,
  getCommodityMarkets,
  isStockMarket,
  isCommodityMarket,
  hasScheduledHours,
  MARKET_CATEGORIES,
};

/**
 * Parse token index from various formats
 * @param {number|string} tokenIndex - TokenIndex in various formats
 * @returns {number|null} Parsed token index number or null
 */
export const parseTokenIndex = (tokenIndex) => {
  if (typeof tokenIndex === 'number') {
    return tokenIndex;
  }
  
  if (typeof tokenIndex === 'string') {
    const match = tokenIndex.match(/TokenIndex\((\d+)\)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    const numValue = parseInt(tokenIndex, 10);
    if (!isNaN(numValue)) {
      return numValue;
    }
  }
  
  return null;
};

/**
 * Parse market index from various formats
 * @param {number|string} marketIndex - MarketIndex in various formats
 * @returns {number|null} Parsed market index number or null
 */
export const parseMarketIndex = (marketIndex) => {
  if (typeof marketIndex === 'number') {
    return marketIndex;
  }
  
  if (typeof marketIndex === 'string') {
    const match = marketIndex.match(/MarketIndex\((\d+)\)/);
    if (match) {
      return parseInt(match[1], 10);
    }
    const numValue = parseInt(marketIndex, 10);
    if (!isNaN(numValue)) {
      return numValue;
    }
  }
  
  return null;
};

/**
 * Get market environment variable name for token index
 * @param {string} marketId - Market ID
 * @returns {string} Environment variable name
 */
export const getTokenIndexEnvVar = (marketId) => {
  const market = getMarketById(marketId);
  if (!market) return null;
  
  const upperId = marketId.toUpperCase();
  return `REACT_APP_PERP_TOKEN_INDEXES_${upperId}`;
};

/**
 * Get market environment variable name for market index
 * @param {string} marketId - Market ID
 * @returns {string} Environment variable name
 */
export const getMarketIndexEnvVar = (marketId) => {
  const market = getMarketById(marketId);
  if (!market) return null;
  
  const upperId = marketId.toUpperCase();
  return `REACT_APP_MARKET_INDEXES_${upperId}`;
};

/**
 * Get market index from environment variable or config
 * @param {string} marketId - Market ID
 * @returns {string} MarketIndex string like "MarketIndex(0)"
 */
export const getMarketIndexFromEnv = (marketId) => {
  const market = getMarketById(marketId);
  if (!market) return null;
  
  const envVar = getMarketIndexEnvVar(marketId);
  const envValue = process.env[envVar];
  
  if (envValue) {
    return envValue;
  }
  
  // Fallback to config
  return formatMarketIndex(market.marketIndex);
};

/**
 * Get token index from environment variable or config
 * @param {string} marketId - Market ID
 * @returns {string} TokenIndex string like "TokenIndex(3)"
 */
export const getTokenIndexFromEnv = (marketId) => {
  const market = getMarketById(marketId);
  if (!market) return null;
  
  const envVar = getTokenIndexEnvVar(marketId);
  const envValue = process.env[envVar];
  
  if (envValue) {
    return envValue;
  }
  
  // Fallback to config
  return formatTokenIndex(market.tokenIndex);
};

/**
 * Validate market configuration
 * @param {Object} market - Market configuration object
 * @returns {Object} Validation result with isValid and errors
 */
export const validateMarket = (market) => {
  const errors = [];
  
  if (!market.id) errors.push('Missing id');
  if (!market.name) errors.push('Missing name');
  if (!market.symbol) errors.push('Missing symbol');
  if (market.tokenIndex === undefined || market.tokenIndex === null) {
    errors.push('Missing tokenIndex');
  }
  if (market.maxLeverage === undefined || market.maxLeverage === null) {
    errors.push('Missing maxLeverage');
  }
  if (!market.category) errors.push('Missing category');
  if (!market.displayName) errors.push('Missing displayName');
  
  return {
    isValid: errors.length === 0,
    errors,
  };
};

/**
 * Get markets sorted by a criteria
 * @param {string} sortBy - Sort criteria ('name', 'symbol', 'tokenIndex', 'marketIndex')
 * @param {string} order - Sort order ('asc' or 'desc')
 * @returns {Array} Sorted array of markets
 */
export const getSortedMarkets = (sortBy = 'name', order = 'asc') => {
  const markets = [...MARKETS];
  
  markets.sort((a, b) => {
    let aVal, bVal;
    
    switch (sortBy) {
      case 'name':
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
        break;
      case 'symbol':
        aVal = a.symbol.toLowerCase();
        bVal = b.symbol.toLowerCase();
        break;
      case 'tokenIndex':
        aVal = a.tokenIndex;
        bVal = b.tokenIndex;
        break;
      case 'marketIndex':
        aVal = a.marketIndex || 9999;
        bVal = b.marketIndex || 9999;
        break;
      default:
        aVal = a.name.toLowerCase();
        bVal = b.name.toLowerCase();
    }
    
    if (aVal < bVal) return order === 'asc' ? -1 : 1;
    if (aVal > bVal) return order === 'asc' ? 1 : -1;
    return 0;
  });
  
  return markets;
};

